import type { AxiosInstance, AxiosProgressEvent, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import type * as rax from 'retry-axios';
import type { BackOffType, HTTP_METHODS } from '@studio/constants/axios';
import { CONFIG_DEFAULTS } from '@studio/constants/axios';

export type HttpMethods = (typeof HTTP_METHODS)[keyof typeof HTTP_METHODS];

export type FileUploadRetryConfigOptions = {
  backoffType?: BackOffType;
  currentRetryAttempt?: number;
  ignoreNetworkErrors?: boolean;
  initialRetryDelay?: number;
  instance: AxiosInstance;
  isResumable?: boolean;
  onRetryAttempt?: (err: Error | FileUploadError) => Promise<Partial<AxiosRequestConfig> | void> | void;
  onSuccess?: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;
  retries: number;
};

export type ClientRetryConfig = AxiosRequestConfig & {
  fileUploadRetryConfig: FileUploadRetryConfig;
};

export class FileUploadRetryConfig {
  retries: number;
  instance: AxiosInstance;
  backoffType: BackOffType;
  initialRetryDelay: number;
  currentRetryAttempt: number;
  isResumable: boolean;
  ignoreNetworkErrors: boolean;
  onSuccess?: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;
  onRetryAttempt?: (err: Error | FileUploadError) => Promise<Partial<AxiosRequestConfig> | void> | void;
  constructor(config: FileUploadRetryConfigOptions) {
    this.retries = config.retries;
    this.instance = config.instance;
    this.backoffType = config.backoffType ?? CONFIG_DEFAULTS.BACKOFF_TYPE;
    this.initialRetryDelay = config.initialRetryDelay ?? CONFIG_DEFAULTS.INITIAL_RETRY_DELAY;
    this.currentRetryAttempt = config.currentRetryAttempt ?? CONFIG_DEFAULTS.CURRENT_RETRY_ATTEMPT;
    this.isResumable = config.isResumable ?? CONFIG_DEFAULTS.IS_RESUMABLE;
    this.ignoreNetworkErrors = config.ignoreNetworkErrors ?? CONFIG_DEFAULTS.IGNORE_NETWORK_ERRORS;
    this.onRetryAttempt = config.onRetryAttempt;
    this.onSuccess = config.onSuccess;
  }
}

type ModifiedRequestConfig = Omit<AxiosRequestConfig, 'onUploadProgress'>;

export type FileUploadRetryInstance = AxiosInstance & {
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
};

export type FileUploadRequestConfig = ModifiedRequestConfig & {
  fileUploadRetryConfig?: FileUploadRetryConfig;
};

export type FileUploadError = AxiosError & {
  config: FileUploadRequestConfig;
};

export interface ApiClientConfig extends AxiosRequestConfig {
  contentType?: string;
  includeTrackingId?: boolean;
  raxConfig?: rax.RetryConfig;
  returnResponse?: boolean;
  token?: string;
}
